package it.maicol07.gamerlogue.services

import at.released.igdbclient.model.Game
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * A game as seen on an external store: its store-specific [uid] (Steam appid, GOG/Epic product id,
 * PSN concept id, Xbox title id) and a human [name] used for the IGDB name-fallback match.
 */
@Serializable
data class ExternalGameRef(val uid: String, val name: String = "")

/**
 * The logged-in user's identity on a store: their [username], optional [avatarUrl] and [profileUrl]
 * (a public page for the account). Shown per connected service in the linked-services list.
 */
@Serializable
data class ServiceProfile(val username: String, val avatarUrl: String? = null, val profileUrl: String? = null)

/**
 * One automation step: navigate the WebView to [url], then run [script].
 *
 * Scripts push their result to Kotlin through the WebView JS bridge (so it works even where
 * `evaluateJavascript` can't return a value, e.g. desktop/CEF). A [SyncScripts.wrap]ped body just
 * assigns the `out` array of `{uid,name}`; the wrapper handles the bridge call and bridge readiness.
 */
data class WebStep(val url: String, val script: String)

/**
 * How one store read resolves. Every read is EITHER same-origin in the WebView OR off-WebView from
 * Kotlin — a connector never needs both for one operation, so this is a two-case choice, not two
 * parallel methods + a flag.
 *
 * - [Web]: run [Web.step] in the WebView and [Web.parse] its bridge result.
 * - [Api]: grab a credential in the WebView (via [Api.credentialStep]), then [Api.fetch] from Kotlin
 *   (no browser CORS); falls back to [Api.default] on a blank credential or a failed fetch.
 */
sealed interface DataSource<T> {
    data class Web<T>(val step: WebStep, val parse: (String?) -> T) : DataSource<T>
    data class Api<T>(
        val credentialStep: WebStep,
        val default: T,
        val fetch: suspend (credential: String) -> T,
    ) : DataSource<T>
}

/**
 * How wishlist writes are performed. [Batch] sends all refs in a single step (stores with a write
 * endpoint, e.g. Steam/GOG); [PerGame] opens each game's store page and acts on it (PSN/Xbox/Epic);
 * [PerGameResolved] first loads an intermediate page ([PerGameResolved.resolve]) whose result's first uid is the real
 * product URL, then acts on that (Nintendo: the IGDB URL is a www.nintendo.com page whose eShop link
 * carries the numeric title id needed to reach the ec.nintendo.com product page with the add button);
 * [SearchByName] searches the store by title and acts on the matching result (Ubisoft: games are
 * name-matched with no IGDB store URL, so there's no `storeUrl` to open a product page with).
 */
sealed interface WishlistWrite {
    data class Batch(val step: (refs: List<ExternalGameRef>) -> WebStep) : WishlistWrite
    data class PerGame(val step: (storeUrl: String) -> WebStep?) : WishlistWrite
    data class PerGameResolved(
        val resolve: (storeUrl: String) -> WebStep,
        val step: (resolvedUrl: String) -> WebStep?,
    ) : WishlistWrite

    data class SearchByName(val step: (name: String) -> WebStep) : WishlistWrite
}

/** Web read delivering a `[{uid,name}, …]` list (owned games / wishlist). */
internal fun webRefs(step: WebStep): DataSource<List<ExternalGameRef>> = DataSource.Web(step, ::parseRefsJson)

/** Web read delivering a `{username, avatarUrl, profileUrl}` profile. */
internal fun webProfile(step: WebStep): DataSource<ServiceProfile?> = DataSource.Web(step, ::parseProfileJson)

/** API read of the user's games; empty on a blank credential or a failed fetch. */
internal fun apiRefs(
    credentialStep: WebStep,
    fetch: suspend (credential: String) -> List<ExternalGameRef>,
): DataSource<List<ExternalGameRef>> = DataSource.Api(credentialStep, emptyList(), fetch)

/** API read of the user's profile; null on a blank credential or a failed fetch. */
internal fun apiProfile(
    credentialStep: WebStep,
    fetch: suspend (credential: String) -> ServiceProfile?,
): DataSource<ServiceProfile?> = DataSource.Api(credentialStep, null, fetch)

/**
 * The single per-service object: identity ([service]), IGDB mapping metadata ([externalGameSource],
 * [storeUrl], [uidFromUrl]) and the WebView automation recipe (login + read/write [DataSource]s).
 *
 * We call no official API from the browser: the user logs into the store in a WebView and we drive
 * that authenticated, same-origin session with injected JavaScript (or, for [DataSource.Api] reads,
 * grab a credential there and fetch from Kotlin). Subclasses override only what differs from the
 * sensible defaults below (most just need URLs + the read/write sources).
 */
abstract class ServiceConnector(
    val service: ExternalService,
    /** Host that identifies a logged-in page of this store. */
    private val host: String,
    /** IGDB `external_games.external_game_source` id for this store. */
    val externalGameSource: Int,
    /** Template (with `{id}`) for the IGDB-indexed store URL, or null if this store has no mapping. */
    private val storeUrlTemplate: String? = null,
) {
    /** IGDB `platform_family` id this store belongs to (PlayStation=1, Xbox=2, …), or null for PC
     *  stores (Steam/GOG/Epic) where games have no single family — used to flag wishlist pushes for
     *  games that don't release on this platform. */
    open val platformFamily: Int? = null

    /** Page where the user signs in (defaults to the store root). */
    open val loginUrl: String get() = "https://$host/"

    /** Origins whose WebView cookies hold this store's authenticated session; cleared on disconnect so
     *  the next connect starts logged out. Defaults to the login origin; override when the session
     *  spans more hosts (e.g. Steam's community domain, PSN's Sony account domains). */
    open val sessionUrls: List<String> get() = listOf(loginUrl)

    /** True once the store session is established. Defaults to a URL check (on a store host, off any
     *  login page); override for stores where the URL alone is ambiguous and the session must be read
     *  from cookies instead (e.g. PSN, whose post-login URL is just the store host). */
    open suspend fun isLoggedIn(currentUrl: String): Boolean =
        currentUrl.contains(host) && LOGIN_MARKERS.none { currentUrl.contains(it) }

    /** JS injected once on the login landing page to kick off the store's sign-in flow (e.g. click the
     *  header sign-in button). Null (default) = the sign-in page is reached directly, no trigger needed.
     *  Fire-and-forget (no bridge result); must be a no-op when its target element isn't present. */
    @Suppress("SameReturnValue")
    open val loginTriggerScript: String? get() = null

    /** Match IGDB `external_games` on the numeric `uid` field instead of `url` — for stores whose
     *  IGDB url is slug-based and can't be rebuilt from the store uid (e.g. GOG). */
    open val idMatchesUid: Boolean = false

    /** The IGDB-indexed store URL for [uid], or null when this service has no URL mapping. */
    fun storeUrl(uid: String): String? = storeUrlTemplate?.replace("{id}", uid)

    /** Extract the store uid back out of an IGDB `url` (inverse of [storeUrl]). */
    open fun uidFromUrl(url: String): String? = null

    /** For [WishlistWrite.SearchByName] connectors with no store URL to confirm a game belongs on this
     *  store: whether [game] plausibly does, so an unrelated backlog game isn't searched for and pushed
     *  onto a store it was never published on (e.g. Ubisoft: check IGDB's involved companies). Ignored
     *  by connectors that always have a store URL to confirm the match instead. */
    open fun matchesPublisher(game: Game): Boolean = true

    // --- Reads + wishlist write. One member per concern; each connector picks Web or Api per read. ---

    /** The user's owned/played games (every connector reads these). */
    abstract val ownedGames: DataSource<List<ExternalGameRef>>

    /** The user's store wishlist, or null if this store exposes none. */
    open val wishlist: DataSource<List<ExternalGameRef>>?
        @Suppress("SameReturnValue")
        get() = null

    /** The signed-in user's profile, or null if this store exposes none. */
    open val profile: DataSource<ServiceProfile?>?
        @Suppress("SameReturnValue")
        get() = null

    /** How to push backlog games onto the store wishlist, or null if writes aren't supported. */
    open val wishlistWrite: WishlistWrite?
        @Suppress("SameReturnValue")
        get() = null

    /** Adjust an IGDB store URL before navigating to it for a push (e.g. strip the locale segment). */
    open fun normalizePushUrl(url: String): String = url

    private companion object {
        val LOGIN_MARKERS = listOf("/login", "signin")
    }
}

// IGDB platform ids for PC stores (Steam/GOG/Epic/Ubisoft): platform_family is null for these
// AND for several unrelated platforms (Android, iOS, Web, DOS, Arcade, …), so an explicit allowlist
// is used instead of "family == null" to avoid tagging a cross-platform title with a mobile/web id.
private val PcPlatformIds = setOf(6, 3, 14) // PC (Windows), Linux, Mac

/**
 * IGDB platform ids [game] releases on, restricted to [ServiceConnector.platformFamily] (or, for PC
 * stores with a null family, to [PcPlatformIds]) — used to autofill `LibraryEntry.platformsIds` on
 * import, since a store [ExternalGameRef] carries no platform info of its own.
 */
fun ServiceConnector.platformIdsFor(game: Game): List<Int> {
    val family = platformFamily
    return game.platforms
        .filter { platform ->
            if (family == null) {
                platform.id.toInt() in PcPlatformIds
            } else {
                platform.platform_family?.id?.toInt() == family
            }
        }
        .map { it.id.toInt() }
}

/** JSON array literal of the refs' uids, e.g. `["12","34"]`, for embedding in an injected script. */
internal fun List<ExternalGameRef>.uidJsonArray(): String =
    joinToString(prefix = "[", postfix = "]") { "\"${it.uid.replace("\"", "")}\"" }

private val resultJson = Json {
    ignoreUnknownKeys = true
    isLenient = true
}

/** Unwrap one optional layer of JSON-string quoting some transports add around the bridge result. */
internal fun cleanJsResult(raw: String?): String? {
    val s = raw?.trim()?.takeUnless { it.isEmpty() || it == "null" } ?: return null
    return runCatching { resultJson.decodeFromString<String>(s) }.getOrDefault(s)
}

/** Parse the `[{uid,name}, …]` JSON a connector script delivers through the WebView JS bridge. */
internal fun parseRefsJson(raw: String?): List<ExternalGameRef> {
    val s = cleanJsResult(raw) ?: return emptyList()
    return runCatching { resultJson.decodeFromString<List<ExternalGameRef>>(s) }.getOrDefault(emptyList())
}

/** Parse the `{username, avatarUrl, profileUrl}` JSON a [ServiceConnector.profile] script delivers
 *  through the bridge. Null on error or an empty username (the wrapper sends `[]` on script failure). */
internal fun parseProfileJson(raw: String?): ServiceProfile? {
    val s = cleanJsResult(raw) ?: return null
    return runCatching { resultJson.decodeFromString<ServiceProfile>(s) }
        .getOrNull()?.takeIf { it.username.isNotBlank() }
}

/**
 * Parse the credential a [DataSource.Api] credential script delivers through the bridge: a plain
 * string (`out = token`). Blank when the script found none — an empty or missing value makes the
 * wrapper fall back to its `[]` sentinel, as a thrown error does.
 */
internal fun parseCredentialJson(raw: String?): String {
    val s = cleanJsResult(raw) ?: return ""
    return if (s.startsWith("[")) "" else s
}

/** Shared JS plumbing for connectors: bridge delivery, DOM waiting helpers, desktop/CEF native polyfill. */
object SyncScripts {
    /** Injected JS object name (see [WebViewJsBridge][com.parkwoocheol.composewebview.WebViewJsBridge]). */
    const val BRIDGE_OBJECT = "GlBridge"

    /** Native-interface object name the bridge proxies to (must match the bridge's nativeInterfaceName). */
    const val BRIDGE_NATIVE = "GlBridgeNative"

    /** Bridge method the wrapped scripts call to deliver their `[{uid,name}, …]` result. */
    const val RESULT_METHOD = "glResult"

    /** Default polling budget of the wait helpers: tries × interval ≈ 15s, under the Kotlin script timeout. */
    private const val DEFAULT_TRIES = 30
    private const val POLL_MS = 500

    /** Default click-then-verify budget of [HELPERS]' `__glClickUntil`. */
    private const val CLICK_ATTEMPTS = 5
    private const val CLICK_SETTLE_MS = 1200

    /**
     * DOM helpers every injected script gets, so no connector hand-rolls a poll loop.
     *
     * - `__glSleep(ms)`
     * - `__glWaitFor(find, tries?, interval?)` — polls `find()` until it returns a truthy value (a
     *   collection must be non-empty) and resolves it; on timeout resolves whatever the last call gave.
     * - `__glWaitForEl(selector, …)` / `__glWaitForAll(selector, …)` — selector shorthands.
     * - `__glVisible(selector)` — first match that is actually rendered, skipping hidden duplicates.
     * - `__glClickUntil(find, confirmed, attempts?, settle?)` — click then verify, with retry: the
     *   React/SSR stores render the button before binding its handler, so a single early click hits a
     *   dead handler and silently does nothing. Resolves `confirmed()`.
     */
    val HELPERS = """
        function __glSleep(ms) { return new Promise(function(r) { setTimeout(r, ms); }); }
        async function __glWaitFor(find, tries, interval) {
            var max = tries || $DEFAULT_TRIES, every = interval || $POLL_MS;
            for (var i = 0; i < max; i++) {
                var found = find();
                // A NodeList/array counts as found only once non-empty; a single element just has to exist.
                if (found && (found.length === undefined || found.length > 0)) return found;
                await __glSleep(every);
            }
            return find();
        }
        function __glWaitForEl(selector, tries, interval) {
            return __glWaitFor(function() { return document.querySelector(selector); }, tries, interval);
        }
        function __glWaitForAll(selector, tries, interval) {
            return __glWaitFor(function() { return document.querySelectorAll(selector); }, tries, interval);
        }
        function __glVisible(selector) {
            return Array.prototype.find.call(
                document.querySelectorAll(selector),
                function(e) { return e.offsetParent !== null; }
            );
        }
        async function __glClickUntil(find, confirmed, attempts, settle) {
            var max = attempts || $CLICK_ATTEMPTS, pause = settle || $CLICK_SETTLE_MS;
            var tried = 0;
            for (var i = 0; i < max && !confirmed(); i++) {
                var el = find();
                if (!el) { await __glSleep($POLL_MS); continue; } // still rendering
                tried++;
                el.click();
                await __glSleep(pause); // let the framework handle the click and its request fire
            }
            console.log('[GL] clickUntil attempts=' + tried + ' ok=' + confirmed());
            return confirmed();
        }
    """.trimIndent()

    /**
     * Fire-and-forget script that clicks the element [finder] returns as soon as it appears, for
     * [ServiceConnector.loginTriggerScript]. That one is injected raw (it delivers no bridge result and
     * so never goes through [wrap]), hence it carries its own copy of [HELPERS]. A no-op when the
     * element never shows up, so it is safe to inject on any load during the login wait.
     */
    fun clickWhenPresent(label: String, finder: String, tries: Int = LOGIN_TRIES): String = """
        (function() {
            $HELPERS
            __glWaitFor($finder, $tries).then(function(el) {
                if (el) { console.log('[GL] $label click'); el.click(); }
            });
        })();
    """.trimIndent()

    /** Logins can sit on a slow redirect chain, so the trigger waits longer than a DOM read. */
    private const val LOGIN_TRIES = 40

    /**
     * Wrap a body that assigns the `out` array into the bridge-delivery protocol: wait for the bridge,
     * run the (possibly async) body, then push `out` to Kotlin. Always sends something (empty on error)
     * so the Kotlin side never waits forever. [HELPERS] are in scope for the body.
     */
    fun wrap(body: String): String = """
        (function() {
            $HELPERS
            // Desktop/CEF re-injects window.$BRIDGE_OBJECT after each navigation but NOT the native
            // polyfill; the CEF message router persists, so re-create the polyfill here. Guarded by
            // window.cefQuery so on Android (real native interface, no cefQuery) we never overwrite it.
            function __glEnsureNative() {
                if (window.$BRIDGE_NATIVE) return;
                if (!window.cefQuery) return;
                window.$BRIDGE_NATIVE = {
                    call: function(method, data, callbackId) {
                        window.cefQuery({
                            request: JSON.stringify({ method: method, data: data, callbackId: callbackId }),
                            onSuccess: function(r) {},
                            onFailure: function(c, m) { console.log('[GL] native fail ' + m); }
                        });
                    }
                };
                console.log('[GL] native polyfill injected');
            }
            function __glSend(out) {
                try {
                    __glEnsureNative();
                    console.log('[GL] send ' + (out ? out.length : 'null'));
                    // Pass the plain value: the bridge JSON-stringifies its `data` argument itself, so
                    // pre-stringifying here would double-encode and break the native-side decode.
                    window.$BRIDGE_OBJECT.call('$RESULT_METHOD', out || []);
                } catch (e) { console.log('[GL] send error ' + e); }
            }
            function __glRun() {
                (async function() {
                    let out = [];
                    try { $body } catch (e) { console.log('[GL] body error ' + e); out = []; }
                    __glSend(out);
                })();
            }
            if (window.$BRIDGE_OBJECT && window.$BRIDGE_OBJECT.call) {
                console.log('[GL] bridge ready'); __glRun();
            } else {
                // Don't wait on the bridge-ready event: the library dispatches a fixed 'AppBridgeReady'
                // (not '${BRIDGE_OBJECT}Ready') and it may have already fired before this listener runs.
                // Poll for the injected object instead — robust on SPA pages that install it late (e.g. GOG).
                console.log('[GL] waiting bridge');
                let __glTries = 0;
                const __glPoll = setInterval(function() {
                    if (window.$BRIDGE_OBJECT && window.$BRIDGE_OBJECT.call) {
                        clearInterval(__glPoll);
                        console.log('[GL] bridge ready (poll)'); __glRun();
                    } else if (++__glTries > 200) { // ~20s, under the Kotlin script timeout
                        clearInterval(__glPoll);
                        console.log('[GL] bridge never appeared'); __glSend([]);
                    }
                }, 100);
            }
        })();
    """.trimIndent()
}
